import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Send, Sparkles, Crown, TrendingUp, Users2, Plane, Globe, CheckCircle2, Circle, ChevronRight } from "lucide-react";

/* Today Profit card — sums today's applicable earnings from actual ledger (server-side).
   Shows breakdown by type (Trading, Team, Referral, Leadership, Shareholder, Staking). */
export function TodayProfitCard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await api.get("/profit/today");
        if (mounted) { setData(data); setErr(false); }
      } catch { if (mounted) setErr(true); }
    };
    load();
    const iv = setInterval(load, 30_000);
    return () => { mounted = false; clearInterval(iv); };
  }, []);

  const LABELS = {
    daily_profit: "Trading Profit",
    referral_bonus: "Referral Bonus",
    team_income: "Team Income",
    leadership_bonus: "Leadership Bonus",
    shareholder_commission: "Shareholder Commission",
    shareholder_bonus: "Shareholder Bonus",
    staking_reward: "Staking Reward",
    rank_bonus: "Rank Bonus",
  };
  const COLORS = {
    daily_profit: "#8AE234",
    referral_bonus: "#00E5FF",
    team_income: "#A855F7",
    leadership_bonus: "#EAB308",
    shareholder_commission: "#F97316",
    shareholder_bonus: "#F97316",
    staking_reward: "#3B82F6",
    rank_bonus: "#EC4899",
  };

  const total = data?.total || 0;
  const items = Object.entries(data?.breakdown || {}).filter(([, v]) => v.count > 0);
  // Feb-2026: compute today's trading percentage (weighted avg across all trading credits)
  const tradingRows = data?.daily_profits || [];
  const totalGross = tradingRows.reduce((s, r) => s + (r.gross || 0), 0);
  const totalInvest = tradingRows.reduce((s, r) => s + ((r.gross || 0) / ((r.profit_pct || 1) / 100)), 0);
  const avgPct = totalInvest > 0 ? (totalGross / totalInvest) * 100 : (tradingRows[0]?.profit_pct || 0);
  const minTodayPct = tradingRows.length ? Math.min(...tradingRows.map(r => r.profit_pct || 0)) : 0;
  const maxTodayPct = tradingRows.length ? Math.max(...tradingRows.map(r => r.profit_pct || 0)) : 0;

  return (
    <div className="nb-card p-5 overflow-hidden relative" data-testid="today-profit-card">
      <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-[#8AE234]/10 blur-2xl pointer-events-none"/>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#8AE234]">
          <Sparkles size={13}/> Today&apos;s Profit
        </div>
        <div className="text-[10px] text-slate-500 uppercase tracking-widest">Live · From ledger</div>
      </div>
      <div className="mt-2 font-mono font-bold text-3xl md:text-4xl text-[#8AE234]" data-testid="today-profit-total">
        ${total.toFixed(2)}
      </div>
      <div className="text-xs text-slate-500 mt-0.5">{data?.transactions_count || 0} transactions · {data?.date || ""}</div>

      {tradingRows.length > 0 && (
        <div className="mt-3 rounded-xl border border-[#8AE234]/25 bg-[#8AE234]/5 p-3" data-testid="today-trading-pct">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#8AE234]">Today&apos;s Trading %</div>
              <div className="font-mono text-xl text-white mt-0.5">{avgPct.toFixed(4)}%</div>
            </div>
            <div className="text-right text-[10px] font-mono text-slate-400 uppercase tracking-widest">
              <div>Range today</div>
              <div className="text-white font-bold">{minTodayPct.toFixed(2)}% – {maxTodayPct.toFixed(2)}%</div>
            </div>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="mt-4 text-sm text-slate-500">No profit credited yet today — your active bots will credit their daily profit at their 24h boundary.</div>
      ) : (
        <div className="mt-4 space-y-2" data-testid="today-profit-breakdown">
          {items.map(([k, v]) => (
            <div key={k} data-testid={`today-profit-${k}`} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2 min-w-0">
                <div className="h-2 w-2 rounded-full shrink-0" style={{background: COLORS[k] || "#94A3B8"}}/>
                <span className="text-slate-300 truncate">{LABELS[k] || k}</span>
                <span className="text-[10px] text-slate-500 font-mono">×{v.count}</span>
              </div>
              <div className="font-mono text-white">${v.total.toFixed(4)}</div>
            </div>
          ))}
        </div>
      )}

      {(data?.daily_profits || []).length > 0 && (
        <details className="mt-4">
          <summary className="cursor-pointer text-xs text-nb-cyan hover:text-white select-none">View trading breakdown (gross → service → net)</summary>
          <div className="mt-2 space-y-1 text-[11px] font-mono">
            {data.daily_profits.map((r) => (
              <div key={r.id} data-testid={`daily-profit-row-${r.id}`} className="grid grid-cols-4 gap-2 rounded-lg border border-white/5 bg-[#070B14] px-2 py-1.5">
                <span className="text-slate-500">Bot-{r.bot_id}</span>
                <span className="text-white">Gross ${r.gross.toFixed(4)}</span>
                <span className="text-red-400">-${r.service_charge.toFixed(4)}</span>
                <span className="text-[#8AE234] text-right">Net ${r.net.toFixed(4)}</span>
              </div>
            ))}
          </div>
        </details>
      )}
      {err && <div className="mt-3 text-[11px] text-red-400">Could not load today&apos;s profit — check connection.</div>}
    </div>
  );
}

/* Tour Eligibility card — Domestic + International with clear qualification progress */
function TourRow({ met, label, sub }) {
  return (
    <div className="flex items-start gap-2 text-xs">
      {met ? <CheckCircle2 size={14} className="text-[#8AE234] mt-0.5 shrink-0"/> : <Circle size={14} className="text-slate-600 mt-0.5 shrink-0"/>}
      <div className="min-w-0">
        <div className={met ? "text-[#8AE234]" : "text-slate-300"}>{label}</div>
        {sub && <div className="text-slate-500 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}
export function TourEligibilityCard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let mounted = true;
    api.get("/tour/eligibility").then(({ data }) => { if (mounted) setData(data); }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  if (!data) return null;
  const dom = data.domestic;
  const intl = data.international;

  return (
    <div className="nb-card p-5 overflow-hidden relative" data-testid="tour-eligibility-card">
      <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-[#F97316]/10 blur-2xl pointer-events-none"/>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#F97316]">
        <Plane size={13}/> Tour Eligibility
      </div>
      <div className="grid md:grid-cols-2 gap-4 mt-3">
        {/* Domestic */}
        <div className={`rounded-xl border p-3 ${dom.eligible ? "border-[#8AE234]/40 bg-[#8AE234]/5" : "border-white/10 bg-[#070B14]"}`} data-testid="tour-domestic">
          <div className="flex items-center gap-2 mb-2">
            <Plane size={14} className={dom.eligible ? "text-[#8AE234]" : "text-slate-500"}/>
            <div className="font-heading text-white text-sm">Domestic Tour</div>
            {dom.eligible && <span className="ml-auto text-[10px] font-mono uppercase text-[#8AE234] bg-[#8AE234]/10 px-2 py-0.5 rounded-full">Qualified</span>}
          </div>
          <div className="space-y-2">
            <TourRow met={dom.requirements.leadership.met}
                 label={`Leadership Qualified (${dom.requirements.leadership.active_directs}/${dom.requirements.leadership.required} active directs)`}
                 sub={dom.requirements.leadership.met ? null : `Need ${dom.requirements.leadership.required - dom.requirements.leadership.active_directs} more active direct`}
            />
            <div className="text-[10px] uppercase tracking-widest text-slate-500 text-center">— OR —</div>
            <TourRow met={dom.requirements.shareholder_usd.met}
                 label={`$${dom.requirements.shareholder_usd.required.toFixed(0)}+ Shareholder`}
                 sub={`Your Total Balance: $${dom.requirements.shareholder_usd.total_holdings.toFixed(2)}`}
            />
          </div>
        </div>
        {/* International */}
        <div className={`rounded-xl border p-3 ${intl.eligible ? "border-[#EAB308]/40 bg-[#EAB308]/5" : "border-white/10 bg-[#070B14]"}`} data-testid="tour-international">
          <div className="flex items-center gap-2 mb-2">
            <Globe size={14} className={intl.eligible ? "text-[#EAB308]" : "text-slate-500"}/>
            <div className="font-heading text-white text-sm">International Tour</div>
            {intl.eligible && <span className="ml-auto text-[10px] font-mono uppercase text-[#EAB308] bg-[#EAB308]/10 px-2 py-0.5 rounded-full">Qualified</span>}
          </div>
          <div className="space-y-2">
            <TourRow met={intl.requirements.diamond.met}
                 label="Diamond Shareholder (15%)"
                 sub={intl.requirements.diamond.met ? "Diamond level achieved" : `Current: ${intl.requirements.diamond.current_level ? intl.requirements.diamond.current_level.toUpperCase() : "None"}`}
            />
            <div className="text-[10px] uppercase tracking-widest text-slate-500 text-center">— OR —</div>
            <TourRow met={intl.requirements.shareholder_plus_team.shareholder_met && intl.requirements.shareholder_plus_team.team_business_met}
                 label={`Eligible Shareholder + $${intl.requirements.shareholder_plus_team.team_business_required.toFixed(0)}+ Team Business`}
                 sub={`Team business: $${intl.requirements.shareholder_plus_team.team_business_usd.toFixed(2)} · Shareholder: ${intl.requirements.shareholder_plus_team.shareholder_met ? "✓" : "✗"}`}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/* Leadership Status card — qualification + projected bonus */
export function LeadershipStatusCard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let mounted = true;
    api.get("/leadership/status").then(({ data }) => { if (mounted) setData(data); }).catch(() => {});
    return () => { mounted = false; };
  }, []);
  if (!data) return null;
  const missing = Math.max(0, data.required_directs - data.active_directs);
  return (
    <div className="nb-card p-5 overflow-hidden relative" data-testid="leadership-status-card">
      <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-[#EAB308]/10 blur-2xl pointer-events-none"/>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#EAB308]">
        <Crown size={13}/> Leadership Status
      </div>
      <div className="mt-2 flex items-center gap-2">
        <div className={`font-heading text-2xl ${data.qualified ? "text-[#8AE234]" : "text-slate-300"}`}>
          {data.qualified ? "Qualified" : "Not Qualified"}
        </div>
        {data.qualified && <span className="text-[10px] font-mono uppercase text-[#8AE234] bg-[#8AE234]/10 px-2 py-0.5 rounded-full">Bonus eligible</span>}
      </div>
      <div className="grid grid-cols-3 gap-3 mt-3 text-xs">
        <div>
          <div className="text-slate-500 uppercase tracking-widest text-[10px]">Active Directs</div>
          <div className="font-mono text-white">{data.active_directs}/{data.required_directs}</div>
        </div>
        <div>
          <div className="text-slate-500 uppercase tracking-widest text-[10px]">Team (30d Total Income)</div>
          <div className="font-mono text-nb-cyan">${(data.team_income_30d ?? data.team_trading_profit_30d ?? 0).toFixed(2)}</div>
        </div>
        <div>
          <div className="text-slate-500 uppercase tracking-widest text-[10px]">Projected {data.bonus_pct}%</div>
          <div className="font-mono text-[#EAB308]">${data.projected_bonus.toFixed(2)}</div>
        </div>
      </div>
      {!data.qualified && (
        <div className="mt-3 text-xs text-slate-400">
          {data.has_active_id ? `Activate ${missing} more direct referral${missing===1?"":"s"} with $${data.min_direct_balance || 100}+ active balance each to qualify. ` : "Activate a bot on your own account first. "}
          Bonus = <span className="text-[#EAB308]">{data.bonus_pct}% of your team&apos;s monthly TOTAL income (excludes staking)</span>.
        </div>
      )}
      {data.total_leadership_bonus_lifetime > 0 && (
        <div className="mt-2 text-[10px] text-slate-500 uppercase tracking-widest">Lifetime earned: <span className="text-white font-mono">${data.total_leadership_bonus_lifetime.toFixed(2)}</span></div>
      )}
    </div>
  );
}

/* Floating Telegram support button — right-middle of viewport */
export function TelegramSupportButton() {
  const [url, setUrl] = useState("");
  useEffect(() => {
    api.get("/public/config").then(({ data }) => setUrl(data.telegram_support_url || "")).catch(() => {});
  }, []);
  if (!url) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="telegram-support-btn"
      className="fixed right-4 top-1/2 -translate-y-1/2 z-40 flex items-center gap-2 rounded-full bg-[#229ED9] hover:bg-[#1B8DC0] text-white px-3 py-2 shadow-lg shadow-[#229ED9]/30 transition-transform hover:scale-105 group"
      aria-label="Open Telegram support"
    >
      <Send size={16} className="rotate-[20deg]"/>
      <span className="text-xs font-semibold hidden sm:inline group-hover:inline">Telegram</span>
    </a>
  );
}
